import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME } from "./attributes";
import { HistoryTableRow } from "./row";
import { HistoryTableRowApplication } from "./row-application";
import { HistoryTableRowCrypto } from "./row-crypto";

import { DepositWithdrawalHistoryResponse } from "@/shared/repository/modules/deposit-withdrawal/repo";
import { HistoryFiatCryptoTab, HistoryTab } from "@/shared/constants/enums";
import { ApplicationHistoryResponse } from "@/shared/repository/modules/application/repo";
import { WalletHistoryResponse } from "@/shared/repository/modules/wallet/repo";

type HistoryTableRowComponent =
  | typeof HistoryTableRow
  | typeof HistoryTableRowApplication
  | typeof HistoryTableRowCrypto;

type HistoryRow =
  | DepositWithdrawalHistoryResponse
  | ApplicationHistoryResponse
  | WalletHistoryResponse;

export interface HistoryTableColumn {
  id: number;
  label: string;
}

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    HistoryTableRow,
    HistoryTableRowApplication,
    HistoryTableRowCrypto,
  },
  props: {
    history: {
      type: <
        PropType<
          | DepositWithdrawalHistoryResponse[]
          | ApplicationHistoryResponse[]
          | WalletHistoryResponse[]
        >
      >Array,
      default: () => [],
    },
    lastFetchCount: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    depositWithdrawal: null,
    application: null,
    remove: null,
    showMore: null,
  },
  data() {
    return {
      historyRepo: this.$projectServices.historyRepo,
    };
  },
  computed: {
    displayedSearchValue(): string {
      return this.historyRepo.search.value;
    },

    displayedColumns(): HistoryTableColumn[] {
      if (this.isCrypto) {
        return ["Дата и время", "Тип", "Сумма", "Валюта", "Статус"].map(
          (label, id) => ({ id, label })
        );
      }

      return [
        "Дата и время",
        "Тип",
        "Сумма",
        "Валюта",
        "Комиссия",
        "Статус",
      ].map((label, id) => ({ id, label }));
    },

    displayedRow(): HistoryTableRowComponent {
      if (this.historyRepo.tab === HistoryTab.application) {
        return HistoryTableRowApplication;
      }

      return this.isCrypto ? HistoryTableRowCrypto : HistoryTableRow;
    },

    filteredRows(): HistoryRow[] {
      const search = this.displayedSearchValue.toLowerCase();
      const rows = this.sortByCreatedAt(this.history as HistoryRow[]);

      if (this.historyRepo.tab === HistoryTab.application) {
        return (rows as ApplicationHistoryResponse[]).filter((item) =>
          `${item.created_at} ${item.amount} ${item.currency_from.name} ${item.currency_to.name}`
            .toLowerCase()
            .includes(search)
        );
      }

      if (this.isCrypto) {
        return (rows as WalletHistoryResponse[]).filter((item) =>
          `${item.created_at} ${item.amount} ${item.coin}`
            .toLowerCase()
            .includes(search)
        );
      }

      return (rows as DepositWithdrawalHistoryResponse[]).filter((item) =>
        `${item.created_at} ${item.amount} ${item.currency.name}`
          .toLowerCase()
          .includes(search)
      );
    },

    displayedRows(): HistoryRow[] {
      return this.filteredRows;
    },

    hasMoreItems(): boolean {
      if (this.lastFetchCount > 0) {
        return this.lastFetchCount >= this.historyRepo.filter.per_page;
      }

      return (
        this.history.length >= this.historyRepo.filter.per_page &&
        this.history.length ===
          this.historyRepo.filter.page * this.historyRepo.filter.per_page
      );
    },

    isApplication(): boolean {
      return this.historyRepo.tab === HistoryTab.application;
    },

    isCrypto(): boolean {
      return this.historyRepo.tabFiatCrypto === HistoryFiatCryptoTab.crypto;
    },

    displayedSkeletonBarWidths(): string[] {
      return ["72%", "58%", "46%", "54%", "40%", "78%"].slice(
        0,
        this.displayedColumns.length
      );
    },
  },
  methods: {
    handleDepositWithdrawal(
      depositWithdrawal: DepositWithdrawalHistoryResponse
    ): void {
      this.$emit("depositWithdrawal", depositWithdrawal);
    },

    handleApplication(application: ApplicationHistoryResponse): void {
      this.$emit("application", application);
    },

    handleRemove(id: number): void {
      this.$emit("remove", id);
    },

    handleShowMore(): void {
      if (!this.loading) {
        this.$emit("showMore");
      }
    },

    rowKey(row: HistoryRow): string {
      const id =
        "id" in row ? row.id : `${row.created_at}-${row.coin}-${row.amount}`;
      return `${this.historyRepo.tab}-${id}`;
    },

    sortByCreatedAt(rows: HistoryRow[]): HistoryRow[] {
      return rows.slice().sort((left, right) => {
        return (
          this.toTimestamp(right.created_at) - this.toTimestamp(left.created_at)
        );
      });
    },

    toTimestamp(value: string): number {
      const normalized = value.replace("T", " ");
      const [date, time = "00:00:00"] = normalized.split(" ");
      const [day, month, year] = date.split(".");

      if (day && month && year) {
        return new Date(`${year}-${month}-${day}T${time}`).getTime();
      }

      return Date.parse(value) || 0;
    },
  },
});
